import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/landing/primitives";
import { Eyebrow, H2, Shot, VERIFY_URL, ALPHA_URL } from "./shared";

const STEPS = [
  { n: "01", t: "Read the market", tools: "TQ Regime · Pulse · News", body: "Know the state before you trade. The seven-state regime engine, the Pulse screener and impact-ranked news tell you if it's a quiet grind, a coiled compression or a stress event.", img: "/of/regime.webp", link: [ALPHA_URL + "/tq-regime", "Open TQ Regime"] },
  { n: "02", t: "Find the setup", tools: "Engine · Screener · Order Flow", body: "Our top-performance engine runs 10+ strategies through multiple filters, scanning markets 24/7 for high-probability buy and sell setups. Then you confirm them on live order flow.", img: "/of/flow.webp", link: [ALPHA_URL, "Open Alpha Flow"] },
  { n: "03", t: "Verify the thesis", tools: "Research · Verify", body: "Every setup the engine fires is published and resolved bar by bar at verify.tradrquant.com, wins and losses. You check the record before you trust the signal.", img: "/of/verify.webp", link: [VERIFY_URL, "Audit the record"] },
];
const DUR = 9000;

export const EdgeIn3 = () => {
  const [i, setI] = useState(0);
  const [key, setKey] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => { setI((n) => (n + 1) % 3); setKey((k) => k + 1); }, DUR);
    return () => clearTimeout(t);
  }, [i, key]);
  return (
    <section id="how" data-testid="edge-in-3" className="border-t border-white/[0.06] bg-[#0A0B0D] py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <Reveal className="max-w-3xl">
          <Eyebrow>How it works</Eyebrow>
          <H2 className="mt-5">Your edge in three minutes. <span className="text-[#7D8390]">Read, find, verify.</span></H2>
        </Reveal>
        <div className="mt-14 grid gap-px bg-white/[0.07] md:grid-cols-3">
          {STEPS.map((x, n) => (
            <button key={x.n} data-testid={`how-step-${x.n}`} onClick={() => { setI(n); setKey((k) => k + 1); }}
              className={`relative bg-[#0A0B0D] p-6 text-left transition-colors md:p-8 ${n === i ? "bg-white/[0.025]" : "hover:bg-white/[0.015]"}`}>
              <span className={`font-mono text-[11px] tracking-[0.2em] ${n === i ? "text-[#BACD1C]" : "text-white/25"}`}>{x.n}</span>
              <span className={`mt-3 block font-display text-xl font-semibold tracking-tight md:text-2xl ${n === i ? "text-[#ECEBE4]" : "text-[#7D8390]"}`}>{x.t}</span>
              <span className="mt-1 block font-mono text-[11px] uppercase tracking-[0.16em] text-[#5A6170]">{x.tools}</span>
              <span className="absolute inset-x-0 bottom-0 h-px bg-white/[0.06]">
                {n === i && <motion.span key={key} className="block h-full bg-[#ECEBE4]/70" initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: DUR / 1000, ease: "linear" }} />}
              </span>
            </button>
          ))}
        </div>
        <div className="mt-10 grid">
          {STEPS.map((s, n) => (
          <div key={s.n} aria-hidden={n !== i} className={`grid items-start gap-10 [grid-area:1/1] transition-opacity duration-500 lg:grid-cols-[1fr_1.6fr] ${n === i ? "opacity-100" : "pointer-events-none opacity-0"}`} data-testid={n === i ? `how-panel-${s.n}` : undefined}>
            <div>
              <p className="text-lg leading-relaxed text-[#9DA3AE]">{s.body}</p>
              <a href={s.link[0]} tabIndex={n === i ? 0 : -1} data-testid={n === i ? `how-link-${s.n}` : undefined} className="mt-6 inline-flex items-center gap-2 border-b border-white/30 pb-1 font-mono text-xs uppercase tracking-[0.16em] text-[#ECEBE4] transition-colors hover:border-[#BACD1C] hover:text-[#BACD1C]">
                {s.link[1]} <ArrowUpRight size={14} />
              </a>
            </div>
            <Shot src={s.img} alt={s.t} />
          </div>
          ))}
        </div>
      </div>
    </section>
  );
};
